import React, { useEffect, useState } from 'react';
import { motion, useScroll, AnimatePresence } from 'framer-motion';
import { ShieldCheckIcon } from 'lucide-react';
import { Hero } from '../components/Hero';
import { SocialProof } from '../components/SocialProof';
import { PriceOffer } from '../components/PriceOffer';
import { FAQ } from '../components/FAQ';
import { CTAButton } from '../components/CTAButton';
import { Footer } from '../components/Footer';
export function LandingPage() {
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const {
    scrollY
  } = useScroll();
  useEffect(() => {
    return scrollY.onChange(latest => {
      // Show sticky CTA after scrolling past hero section (approx 600px)
      setShowStickyCTA(latest > 600);
    });
  }, [scrollY]);
  return <main className="min-h-screen bg-white font-sans text-slate-900 selection:bg-rose-200 selection:text-rose-900 pb-24 md:pb-0" data-id="element-198">
      <Hero data-id="element-199" />

      {/* SECTION 1: THE HOOK */}
      <section className="py-20 px-4 bg-white" data-id="element-200">
        <div className="max-w-3xl mx-auto text-center" data-id="element-201">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-202">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 leading-tight" data-id="element-203">
              Have you ever stopped to think that you are just{' '}
              <span className="text-rose-600 border-b-4 border-rose-200" data-id="element-204">
                one nail set away
              </span>{' '}
              from changing your bank account forever?
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed" data-id="element-205">
              Look around you. In every corner of your city, there are women
              desperately looking for someone they can trust with their hands.
              The question isn't whether there are clients... the question is:{' '}
              <strong className="text-slate-900" data-id="element-206">
                Why aren't they paying YOU yet?
              </strong>
            </p>
          </motion.div>
        </div>
      </section>

      {/* SECTION 2: AGITATING THE PAIN */}
      <section className="py-20 px-4 bg-rose-50" data-id="element-207">
        <div className="max-w-4xl mx-auto" data-id="element-208">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-12 text-center" data-id="element-209">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-210">
              I know you're tired.
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto" data-id="element-211">
              Tired of waking up to a 9-to-5 that drains your energy. Tired of
              seeing the cost of rent and groceries skyrocket in the U.S. while
              your paycheck stays exactly the same.
            </p>
          </motion.div>

          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-rose-100" data-id="element-212">
            <p className="text-lg font-medium text-slate-800 mb-6 text-center" data-id="element-213">
              You feel you have the talent, but fear holds you back:
            </p>

            <div className="space-y-4 mb-10 max-w-2xl mx-auto" data-id="element-214">
              {['"What if I get fined for not having a license?"', '"What if I buy the wrong kit and waste my savings?"', '"What if the nails fall off after two days?"'].map((fear, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="bg-slate-50 p-4 rounded-xl border-l-4 border-rose-400 text-slate-700 font-medium italic" data-id="element-215">
                  {fear}
                </motion.div>)}
            </div>

            <div className="text-center" data-id="element-216">
              <p className="text-xl text-slate-700 mb-8" data-id="element-217">
                That fear is costing you. Every month you spend doubting is{' '}
                <strong className="text-rose-600 font-bold" data-id="element-218">
                  $5,000 you're leaving on the table
                </strong>
                . This is your sign to stop being a spectator and start being
                the CEO.
              </p>
              <CTAButton scrollTo="price-offer-cta" data-id="element-219">
                YES, I WANT MY FREEDOM
              </CTAButton>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: THE REVELATION */}
      <section className="py-24 px-4 bg-white" data-id="element-220">
        <div className="max-w-3xl mx-auto" data-id="element-221">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-222">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 text-center" data-id="element-223">
              Here's the secret the $15,000 beauty schools don't want you to
              know:
            </h2>

            <div className="bg-rose-600 text-white p-8 rounded-2xl shadow-lg mb-10 transform -rotate-1" data-id="element-224">
              <p className="text-2xl md:text-3xl font-bold text-center leading-tight" data-id="element-225">
                Doing "pretty nails" is only 20% of the success. <br data-id="element-226" />
                <span className="text-rose-200" data-id="element-227">
                  The other 80% is the Business Blueprint.
                </span>
              </p>
            </div>

            <div className="prose prose-lg prose-slate mx-auto text-slate-600" data-id="element-228">
              <p className="mb-6 text-lg leading-relaxed" data-id="element-229">
                In the United States, the wealthiest Nail Techs aren't the ones
                who can draw the most complex art. They are the ones who know
                how to register their LLC, how to use the TikTok algorithm to
                bring in local neighbors, and how to prep the nail chemically so
                the set stays flawless for 4 weeks.
              </p>
              <p className="text-lg leading-relaxed font-medium text-slate-900 bg-rose-50 p-6 rounded-xl border border-rose-100" data-id="element-230">
                That's exactly what I discovered after months of trial and
                error... and I've packaged it all for you.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <SocialProof data-id="element-231" />
      <PriceOffer data-id="element-232" />
      <FAQ data-id="element-233" />

      {/* SECTION 8: THE GUARANTEE */}
      <section className="py-20 px-4 bg-white" data-id="element-234">
        <div className="max-w-3xl mx-auto" data-id="element-235">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="bg-slate-900 text-white rounded-3xl p-8 md:p-12 text-center relative overflow-hidden shadow-2xl" data-id="element-236">
            <div className="absolute top-0 right-0 -mt-10 -mr-10 text-slate-800 opacity-50" data-id="element-237">
              <ShieldCheckIcon className="w-64 h-64" data-id="element-238" />
            </div>

            <div className="relative z-10" data-id="element-239">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-rose-500 rounded-full mb-6 shadow-lg shadow-rose-500/30" data-id="element-240">
                <ShieldCheckIcon className="w-10 h-10 text-white" data-id="element-241" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-242">
                7-Day Master Guarantee
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed mb-8" data-id="element-243">
                Try the system. Download the bonuses. Read the guides. If in 7
                days you don't feel this will change your financial future,
                email us and we'll refund 100% of your money. No questions
                asked.{' '}
                <strong className="text-white" data-id="element-244">All the risk is on me.</strong>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 9: THE CLOSING */}
      <section className="py-24 px-4 bg-rose-50 border-t border-rose-100" data-id="element-245">
        <div className="max-w-4xl mx-auto text-center" data-id="element-246">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-247">
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-12" data-id="element-248">
              You have two paths right now:
            </h2>

            <div className="grid md:grid-cols-2 gap-8 mb-12 text-left" data-id="element-249">
              <div className="bg-white p-8 rounded-2xl shadow-md border border-slate-200 opacity-75" data-id="element-250">
                <h3 className="text-xl font-bold text-slate-500 mb-4 uppercase tracking-wider" data-id="element-251">
                  Path A
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-252">
                  Close this page, stay in your current job, complain about
                  inflation, and keep wishing for change while you watch other
                  women live the life you want.
                </p>
              </div>

              <div className="bg-white p-8 rounded-2xl shadow-xl border-2 border-rose-400 relative transform md:-translate-y-4" data-id="element-253">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-rose-500 text-white px-4 py-1 rounded-full text-sm font-bold uppercase tracking-wider" data-id="element-254">
                  The Smart Choice
                </div>
                <h3 className="text-xl font-bold text-rose-600 mb-4 uppercase tracking-wider" data-id="element-255">
                  Path B
                </h3>
                <p className="text-slate-800 font-medium leading-relaxed" data-id="element-256">
                  Invest less than the cost of a dinner out, take control of
                  your career, and by next month, you could be celebrating your
                  first $1,000 made with your own hands.
                </p>
              </div>
            </div>

            <p className="text-2xl font-bold text-slate-900 mb-8" data-id="element-257">
              The choice is yours.
            </p>

            <CTAButton size="xl" className="w-full md:w-auto" scrollTo="price-offer-cta" data-id="element-258">
              YES, I WANT MY FREEDOM
            </CTAButton>
          </motion.div>
        </div>
      </section>

      {/* Sticky Mobile CTA */}
      <AnimatePresence data-id="element-259">
        {showStickyCTA && <motion.div initial={{
        y: 100
      }} animate={{
        y: 0
      }} exit={{
        y: 100
      }} className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-slate-200 shadow-[0_-10px_20px_-10px_rgba(0,0,0,0.1)] z-50 md:hidden" data-id="element-260">
            <CTAButton fullWidth size="md" scrollTo="price-offer-cta" data-id="element-261">
              YES, I WANT MY FREEDOM
            </CTAButton>
          </motion.div>}
      </AnimatePresence>

      <Footer data-id="element-262" />
    </main>;
}